import { Sparkles, Shield, Clock, Award, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../Navbar';
import Footer from '../../Footer';
import type { IdentityStep } from '../types';

interface WelcomeScreenModalProps {
  onStepChange: (step: IdentityStep) => void;
}

export const WelcomeScreenModal = ({ onStepChange }: WelcomeScreenModalProps) => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Shared Navbar */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-1 px-6 lg:px-8 py-12 pt-24">
        <div className="max-w-7xl mx-auto text-center space-y-12">
          {/* Hero Section */}
          <div className="space-y-8">
            {/* Main Heading */}
            <div className="space-y-4">
              <h1
                className="text-4xl md:text-6xl font-bold leading-tight"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Autar
              </h1>

              <p
                className="text-xl md:text-2xl leading-relaxed max-w-3xl mx-auto"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Stop managing technical debt. Let Autar autonomously maintain, refactor and scale
                your codebase so you can focus on shipping value.
              </p>
            </div>

            {/* Feature Highlights */}
            <div
              className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" style={{ color: 'var(--color-accent-teal)' }} />
                <span>No code access required</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: 'var(--color-accent-teal)' }} />
                <span>Results in minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" style={{ color: 'var(--color-accent-teal)' }} />
                <span>No credit card needed</span>
              </div>
            </div>
          </div>

          {/* Authentication Options */}
          <div className="space-y-6">
            <div className="grid gap-4 max-w-md mx-auto">
              {/* Get Started - Primary Action */}
              <Link
                to="/signup"
                className="btn-primary flex items-center justify-center gap-3 w-full group py-4"
                aria-label="Get started with Autar"
              >
                <span className="text-base">Get Started</span>
              </Link>

              {/* Try Demo - Secondary Action */}
              <button
                onClick={() => onStepChange('demo')}
                className="bg-white dark:bg-gray-800 flex flex-col items-center justify-center w-full px-6 py-4 rounded-lg border transition-all duration-200 hover:border-accent"
                style={{
                  borderColor: 'var(--color-border-default)',
                  color: 'var(--color-text-secondary)',
                  backgroundColor: 'var(--color-bg-secondary)',
                }}
                aria-label="Try demo mode without creating an account"
              >
                <div className="flex items-center gap-3">
                  <Play className="w-5 h-5" />
                  <span className="text-base">Try Demo</span>
                </div>
                <span className="text-xs mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
                  No login required
                </span>
              </button>
            </div>

            {/* Additional Info */}
            <div className="space-y-3">
              <p
                className="text-sm max-w-md mx-auto leading-relaxed"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Automate your maintenance with insights gathered from thousands of repos.
              </p>

              <p
                className="text-xs whitespace-nowrap"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                By continuing, you agree to our{' '}
                <Link
                  to="https://autar.ai/terms"
                  className="underline hover:no-underline transition-all duration-200"
                  style={{ color: 'var(--color-accent-teal)' }}
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <a
                  href="https://autar.ai/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline transition-all duration-200"
                  style={{ color: 'var(--color-accent-teal)' }}
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>

          {/* Value Proposition Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-16">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-left space-y-4 hover:border-accent transition-all duration-300">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-teal)' }}
              >
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Zero-Risk Analysis
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Autar performs non-destructive analysis on a sandbox environment. Your production
                source remains untouched and secure.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-left space-y-4 hover:border-accent transition-all duration-300">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-blue)' }}
              >
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Actionable Intelligence
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Skip the cryptic error logs. Autar translates technical debt into clear steps you
                can act on immediately.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-left space-y-4 hover:border-accent transition-all duration-300">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-green)' }}
              >
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Elite Coding Standards
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Adopt the exact linting and refactoring patterns used by world-class engineering
                teams.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};
